import { formatLocalDate } from '../utils/localDateTime';
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, orderBy, limit, getDocs, getDoc, doc } from 'firebase/firestore';
import type { Page } from '@types';

interface CountryIntelPageProps {
    iso3: string;
    organizationId: string;
    onNavigate: (nav: { page: Page; params?: any }) => void;
    onBack: () => void;
}

interface PmesiiDimension {
    status?: string;
    detail?: string;
    score?: number;
}

interface CountryAnalysis {
    riskScore: number;
    executiveSummary: string;
    pmesii: Record<string, PmesiiDimension | string>;
    threatActors?: { name: string; type: string; summary?: string }[];
    lastUpdatedAt?: any;
}

interface RiskEvent {
    id: string;
    title: string;
    category: string;
    riskScore: number;
    summary: string;
    processedAt: string;
    severity: string;
}

interface SrmProject {
    id: string;
    project_name: string;
    status: string;
    risk_rating?: string;
    framework_type?: string;
}

const ISO3_TO_NAME: Record<string, string> = {
    USA: 'United States', GBR: 'United Kingdom', CAN: 'Canada', AUS: 'Australia',
    DEU: 'Germany', FRA: 'France', ITA: 'Italy', ESP: 'Spain', CHN: 'China',
    RUS: 'Russia', IRN: 'Iran', IRQ: 'Iraq', SYR: 'Syria', AFG: 'Afghanistan',
    PAK: 'Pakistan', IND: 'India', BRA: 'Brazil', MEX: 'Mexico', ZAF: 'South Africa',
    NGA: 'Nigeria', ETH: 'Ethiopia', EGY: 'Egypt', ISR: 'Israel', SAU: 'Saudi Arabia',
    TUR: 'Turkey', UKR: 'Ukraine', KOR: 'South Korea', JPN: 'Japan',
};

const PMESII_COLORS: Record<string, { base: string; fill: string }> = {
    Political:      { base: 'bg-blue-100',   fill: 'bg-blue-500' },
    Military:       { base: 'bg-red-100',    fill: 'bg-red-500' },
    Economic:       { base: 'bg-emerald-100', fill: 'bg-emerald-500' },
    Social:         { base: 'bg-violet-100', fill: 'bg-violet-500' },
    Infrastructure: { base: 'bg-amber-100',  fill: 'bg-amber-500' },
    Information:    { base: 'bg-cyan-100',   fill: 'bg-cyan-500' },
};

const getRiskBadge = (score: number) => {
    if (score >= 80) return 'bg-red-100 text-red-700 border-red-200';
    if (score >= 60) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (score >= 40) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-green-100 text-green-700 border-green-200';
};

const getRiskLabel = (score: number) => {
    if (score >= 80) return 'Critical';
    if (score >= 60) return 'High';
    if (score >= 40) return 'Elevated';
    return 'Low';
};

const getStatusBadge = (status: string) => {
    if (status === 'Active') return 'bg-green-100 text-green-700';
    if (status === 'Review') return 'bg-amber-100 text-amber-700';
    if (status === 'Draft') return 'bg-slate-100 text-slate-600';
    return 'bg-slate-100 text-slate-500';
};

const CountryIntelPage: React.FC<CountryIntelPageProps> = ({ iso3, organizationId, onNavigate, onBack }) => {
    const [analysis, setAnalysis] = useState<CountryAnalysis | null>(null);
    const [events, setEvents] = useState<RiskEvent[]>([]);
    const [projects, setProjects] = useState<SrmProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [reportGenerating, setReportGenerating] = useState<'micro' | 'macro' | 'osint' | null>(null);

    const countryName = ISO3_TO_NAME[iso3] || iso3;

    const loadAnalysis = useCallback(async () => {
        try {
            const snap = await getDoc(doc(db, 'daily_country_analysis', iso3));
            if (snap.exists()) {
                const d = snap.data() as any;
                setAnalysis({
                    riskScore: d.riskScore ?? d.overallGeopoliticalRiskScore ?? 50,
                    executiveSummary: d.executiveSummary ?? d.latestIncidentSummary ?? 'No strategic summary available.',
                    pmesii: d.pmesii ?? {},
                    threatActors: d.threatActors ?? d.knownThreatActors ?? [],
                    lastUpdatedAt: d.lastUpdatedAt
                });
            }
        } catch (e) {
            console.error('[CountryIntelPage] Failed to load analysis', e);
        } finally {
            setLoading(false);
        }
    }, [iso3]);

    const loadEvents = useCallback(async () => {
        const mapDoc = (d: any) => {
            const data = d.data();
            return {
                id: d.id,
                title: data.analysis?.intel_title || data.title || 'Untitled Event',
                category: data.analysis?.category || data.category || 'Intelligence',
                riskScore: data.analysis?.risk_score ?? data.riskScore ?? 0,
                summary: data.analysis?.incident_summary || data.summary || data.content || 'No summary available.',
                processedAt: data.processedAt?.toDate
                    ? formatLocalDate(data.processedAt.toDate())
                    : 'Recent',
                severity: data.alertSeverity || 'Medium'
            };
        };

        try {
            // Phase 1: Query on geo.country_iso3 (covers GDELT events where iso3 was resolved)
            const q1 = query(
                collection(db, 'risk_events'),
                where('geo.country_iso3', '==', iso3),
                orderBy('processedAt', 'desc'),
                limit(15)
            );
            const snap1 = await getDocs(q1);

            if (!snap1.empty) {
                setEvents(snap1.docs.map(mapDoc));
                return;
            }

            // Phase 2: Fall back to top-level iso3 field (covers ReliefWeb / other sources)
            const q2 = query(
                collection(db, 'risk_events'),
                where('iso3', '==', iso3),
                orderBy('processedAt', 'desc'),
                limit(15)
            );
            const snap2 = await getDocs(q2);

            setEvents(snap2.docs.map(mapDoc));
        } catch (e) {
            console.error('[CountryIntelPage] Failed to load events', e);
        } finally {
            setEventsLoading(false);
        }
    }, [iso3]);

    const loadProjects = useCallback(async () => {
        try {
            const q = query(
                collection(db, 'srm_projects'),
                where('organizationId', '==', organizationId),
                where('country_iso3', '==', iso3),
                limit(8)
            );
            const snap = await getDocs(q);
            setProjects(snap.docs.map(d => ({
                id: d.id,
                project_name: d.data().project_name || 'Untitled Project',
                status: d.data().status || 'Draft',
                risk_rating: d.data().risk_rating,
                framework_type: d.data().framework_type
            })));
        } catch (e) {
            console.error('[CountryIntelPage] Failed to load projects', e);
        } finally {
            setProjectsLoading(false);
        }
    }, [iso3, organizationId]);

    useEffect(() => {
        loadAnalysis();
        loadEvents();
        loadProjects();
    }, [loadAnalysis, loadEvents, loadProjects]);

    const extractPmesiiScore = (val: PmesiiDimension | string | undefined): number => {
        if (!val) return 50;
        if (typeof val === 'string') {
            const s = val.toLowerCase();
            if (s.includes('critical')) return 85;
            if (s.includes('elevated') || s.includes('high')) return 65;
            if (s.includes('stable') || s.includes('low')) return 30;
            return 50;
        }
        if (typeof val.score === 'number') return val.score;
        const status = (val.status || '').toLowerCase();
        if (status === 'critical') return 85;
        if (status === 'elevated') return 65;
        if (status === 'stable') return 30;
        return 50;
    };

    const getPmesiiText = (val: PmesiiDimension | string | undefined): string => {
        if (!val) return 'No data';
        if (typeof val === 'string') return val.slice(0, 120);
        return val.detail?.slice(0, 120) || val.status || 'No data';
    };

    const pmesiiKeys = ['Political', 'Military', 'Economic', 'Social', 'Infrastructure', 'Information'];

    return (
        <div id={`country-intel-${iso3}`} className="animate-fade-in bg-slate-50 min-h-screen">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            id="country-intel-back"
                            onClick={onBack}
                            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-blue-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            GSOC Command
                        </button>
                        <div className="w-px h-5 bg-slate-200" />
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">{countryName}</h1>
                            <p className="text-xs text-slate-500 font-mono">{iso3} · Country Intelligence Profile</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {analysis && (
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-bold ${getRiskBadge(analysis.riskScore)}`}>
                                <span className="w-2 h-2 rounded-full bg-current opacity-70" />
                                {getRiskLabel(analysis.riskScore)} · {analysis.riskScore}
                            </span>
                        )}
                        <button
                            id="country-intel-srm-new"
                            onClick={() => onNavigate({ page: 'srm_command_center', params: { prefilledCountry: iso3 } })}
                            className="bg-slate-900 hover:bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                        >
                            + New SRM Project
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 xl:grid-cols-3 gap-6">

                {/* ── Left Column (2/3): PMESII + Events ── */}
                <div className="xl:col-span-2 space-y-6">

                    {/* Executive Summary */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">Strategic Overview</h2>
                        {loading ? (
                            <div className="h-16 bg-slate-100 animate-pulse rounded-lg" />
                        ) : (
                            <p className="text-slate-700 text-sm leading-relaxed">
                                {analysis?.executiveSummary || 'No strategic analysis available for this country. Run a Country Analysis to generate intelligence.'}
                            </p>
                        )}
                    </div>

                    {/* PMESII Score Bars */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">PMESII Assessment</h2>
                            <span className="text-xs text-slate-400">Higher = Greater Risk</span>
                        </div>
                        {loading ? (
                            <div className="space-y-3">
                                {pmesiiKeys.map(k => <div key={k} className="h-8 bg-slate-100 animate-pulse rounded" />)}
                            </div>
                        ) : analysis?.pmesii && Object.keys(analysis.pmesii).length > 0 ? (
                            <div className="space-y-3">
                                {pmesiiKeys.map(key => {
                                    const val = analysis.pmesii[key] || analysis.pmesii[key.toLowerCase()];
                                    const score = extractPmesiiScore(val);
                                    const text = getPmesiiText(val);
                                    const colors = PMESII_COLORS[key] || { base: 'bg-slate-100', fill: 'bg-slate-500' };
                                    return (
                                        <div key={key}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-semibold text-slate-700">{key}</span>
                                                <span className="text-xs text-slate-500">{score}/100</span>
                                            </div>
                                            <div className={`w-full h-2.5 rounded-full ${colors.base}`}>
                                                <div
                                                    className={`h-2.5 rounded-full ${colors.fill} transition-all duration-700`}
                                                    style={{ width: `${Math.min(score, 100)}%` }}
                                                />
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1 truncate" title={text}>{text}...</p>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-400 italic">No PMESII data. Run a Scheduled Country Analysis to populate.</p>
                        )}
                    </div>

                    {/* Live Risk Events */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Recent Risk Events</h2>
                            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                Live Feed
                            </span>
                        </div>
                        {eventsLoading ? (
                            <div className="space-y-3">
                                {[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 animate-pulse rounded-lg" />)}
                            </div>
                        ) : events.length === 0 ? (
                            <p className="text-sm text-slate-400 italic">No risk events recorded for {countryName} in the last 7 days.</p>
                        ) : (
                            <div className="space-y-2">
                                {events.map(evt => (
                                    <div key={evt.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-300 transition-colors">
                                        <span className={`mt-0.5 flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-bold border ${getRiskBadge(evt.riskScore)}`}>
                                            {evt.riskScore}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{evt.title}</p>
                                            <p className="text-xs text-slate-500 mt-0.5 truncate">{evt.category} · {evt.processedAt}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Right Column (1/3): Projects + Threat Actors ── */}
                <div className="space-y-6">

                    {/* Active SRM Projects */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-4">SRM Projects in {countryName}</h2>
                        {projectsLoading ? (
                            <div className="space-y-2">
                                {[1,2].map(i => <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />)}
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="text-center py-6">
                                <p className="text-sm text-slate-400 mb-3">No active projects in this country</p>
                                <button
                                    id={`country-intel-create-project-${iso3}`}
                                    onClick={() => onNavigate({ page: 'srm_command_center', params: { prefilledCountry: iso3 } })}
                                    className="text-xs font-semibold text-blue-600 hover:underline"
                                >
                                    Create First Project →
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {projects.map(proj => (
                                    <button
                                        key={proj.id}
                                        id={`country-intel-project-${proj.id}`}
                                        onClick={() => onNavigate({ page: 'srm_command_center', params: { projectId: proj.id } })}
                                        className="w-full text-left p-3 rounded-lg border border-slate-100 hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{proj.project_name}</p>
                                            <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${getStatusBadge(proj.status)}`}>
                                                {proj.status}
                                            </span>
                                        </div>
                                        {proj.framework_type && (
                                            <p className="text-xs text-slate-500 mt-0.5">{proj.framework_type}</p>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Threat Actors */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-4">Known Threat Actors</h2>
                        {loading ? (
                            <div className="space-y-2">
                                {[1,2].map(i => <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}
                            </div>
                        ) : !analysis?.threatActors || analysis.threatActors.length === 0 ? (
                            <p className="text-sm text-slate-400 italic">No threat actors documented. Run a Threat Group Analysis to populate.</p>
                        ) : (
                            <div className="space-y-2">
                                {analysis.threatActors.slice(0, 6).map((actor, idx) => (
                                    <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-100">
                                        <p className="text-sm font-semibold text-slate-800">{actor.name}</p>
                                        <p className="text-xs text-slate-500">{actor.type}</p>
                                        {actor.summary && (
                                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{actor.summary}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Generate Intelligence Reports */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-1">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Generate Intelligence Reports</h2>
                        </div>
                        <p className="text-xs text-slate-400 mb-4">Produce formal AI-authored intelligence documents for {countryName}.</p>
                        <div className="space-y-2">

                            {/* Daily Micro Analysis — reads cached Firestore doc */}
                            <button
                                id={`country-intel-daily-micro-${iso3}`}
                                disabled={!!reportGenerating}
                                onClick={() => {
                                    setReportGenerating('micro');
                                    onNavigate({
                                        page: 'gsocDashboard',
                                        params: {
                                            suiteName: 'Threat Analysis Hub',
                                            autoRunReport: 'dailyMicro',
                                            preselectedCountry: iso3
                                        }
                                    });
                                }}
                                className="w-full flex items-start gap-3 px-4 py-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-left"
                            >
                                <div className="flex-shrink-0 mt-0.5 p-1.5 bg-blue-600 rounded-md">
                                    {reportGenerating === 'micro' ? (
                                        <svg className="w-3.5 h-3.5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                        </svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-blue-800">Daily Micro Analysis</p>
                                    <p className="text-xs text-blue-600 mt-0.5">Reads cached 24-hr intelligence snapshot</p>
                                </div>
                            </button>

                            {/* Deep Dive — live AI generation */}
                            <button
                                id={`country-intel-deep-dive-${iso3}`}
                                disabled={!!reportGenerating}
                                onClick={() => {
                                    setReportGenerating('macro');
                                    onNavigate({
                                        page: 'gsocDashboard',
                                        params: {
                                            suiteName: 'Threat Analysis Hub',
                                            autoRunReport: 'macroDeepDive',
                                            preselectedCountry: iso3
                                        }
                                    });
                                }}
                                className="w-full flex items-start gap-3 px-4 py-3 rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-left"
                            >
                                <div className="flex-shrink-0 mt-0.5 p-1.5 bg-slate-800 rounded-md">
                                    {reportGenerating === 'macro' ? (
                                        <svg className="w-3.5 h-3.5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                        </svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-slate-800">Comprehensive Deep Dive</p>
                                    <p className="text-xs text-slate-500 mt-0.5">Live Insights generated macro geopolitical report</p>
                                </div>
                            </button>

                            {/* OSINT Dossier — threat group tactical intel */}
                            <button
                                id={`country-intel-osint-${iso3}`}
                                disabled={!!reportGenerating}
                                onClick={() => {
                                    setReportGenerating('osint');
                                    onNavigate({
                                        page: 'gsocDashboard',
                                        params: {
                                            suiteName: 'Threat Analysis Hub',
                                            autoRunReport: 'osintDossier',
                                            preselectedCountry: iso3
                                        }
                                    });
                                }}
                                className="w-full flex items-start gap-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-left"
                            >
                                <div className="flex-shrink-0 mt-0.5 p-1.5 bg-red-600 rounded-md">
                                    {reportGenerating === 'osint' ? (
                                        <svg className="w-3.5 h-3.5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                        </svg>
                                    ) : (
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-red-800">OSINT Threat Dossier</p>
                                    <p className="text-xs text-red-600 mt-0.5">Threat group tactical intelligence compilation</p>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-4">Quick Actions</h2>
                        <div className="space-y-2">
                            <button
                                id={`country-intel-run-analysis-${iso3}`}
                                onClick={() => onNavigate({ page: 'suiteDashboard', params: { suiteName: 'Country Analysis', initialSearch: countryName } })}
                                className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 text-sm font-medium text-slate-700 transition-all flex items-center justify-between"
                            >
                                <span>Run Country Analysis</span>
                                <span className="text-slate-400">→</span>
                            </button>
                            <button
                                id={`country-intel-threat-group-${iso3}`}
                                onClick={() => onNavigate({ page: 'gtg_dashboard' })}
                                className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-red-200 hover:bg-red-50/50 text-sm font-medium text-slate-700 transition-all flex items-center justify-between"
                            >
                                <span>Threat Group Analysis</span>
                                <span className="text-slate-400">→</span>
                            </button>
                            <button
                                id={`country-intel-gsoc-back-${iso3}`}
                                onClick={onBack}
                                className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-slate-400 text-sm font-medium text-slate-700 transition-all flex items-center justify-between"
                            >
                                <span>Back to GSOC Map</span>
                                <span className="text-slate-400">↩</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CountryIntelPage;
